import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { generateBlog } from 'src/common/Scraper';
import {
  BlogRunPayload,
  BlogRunResponse
} from '../../types/blog-generation-agent.types';

interface UseGenerateBlogProps {
  onSuccess?: (data: BlogRunResponse) => void;
  onError?: (error: any) => void;
}

import { posthogService, PostHogEvent } from 'src/common/services/posthogService';

/**
 * A custom mutation hook that triggers the blog generation process
 * by calling the `generateBlog` scraper API with the provided payload,
 * and invalidates the blog blueprints query on success.
 */
function useGenerateBlog({ onSuccess, onError }: UseGenerateBlogProps = {}) {
  const queryClient = useQueryClient();
  const { _id: siteId, user_id: userId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  );

  return useMutation<
    BlogRunResponse,
    Error,
    Omit<BlogRunPayload, 'user_id' | 'site_id'>
  >({
    mutationFn: (payload: Omit<BlogRunPayload, 'user_id' | 'site_id'>) =>
      generateBlog({
        ...payload,
        user_id: userId,
        site_id: siteId
      } as BlogRunPayload) as Promise<BlogRunResponse>,
    mutationKey: ['generate-blog', userId, siteId],
    onSuccess: (data, variables) => {
      // Track Orbit AI Blog Generation completion in PostHog
      // Captures site ID, user ID, blueprint ID, and whether prompt suggestions were passed
      posthogService.track(PostHogEvent.ORBIT_AI_BLOG_GENERATED, {
        site_id: siteId,
        user_id: userId,
        blueprint_id: variables?.blueprint?._id,
        has_suggestion: Boolean(variables?.suggestion)
      });

      queryClient.invalidateQueries({
        queryKey: ['blog-blueprints', userId, siteId]
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    }
  });
}

export default useGenerateBlog;
